/**
 * Search History
 * Shopify Theme: Sense
 *
 * Features:
 * - Stores last 8 searches in sessionStorage
 * - Newest search appears first
 * - Prevents duplicate searches
 * - Supports Arabic + English
 * - Shows recent searches when search input is empty/focused
 * - Clicking a recent search performs that search again
 * - "Clear history" removes all saved searches
 *
 * Version: 1.1.0
 */

(function () {
  'use strict';

  /* ==========================================================
   * SETTINGS
   * ========================================================== */

  const STORAGE_KEY = 'shopify_recent_searches';
  const MAX_SEARCHES = 8;
  const DEBUG = false;

  /* ==========================================================
   * LOGGING
   * ========================================================== */

  function log() {
    if (!DEBUG) return;

    console.log(
      '[Search History]',
      ...arguments
    );
  }

  /* ==========================================================
   * LANGUAGE
   * ========================================================== */

  function isArabic() {
    const lang = (
      document.documentElement.lang || ''
    ).toLowerCase();

    return (
      lang === 'ar' ||
      lang.startsWith('ar-')
    );
  }

  function getTranslations() {
    if (isArabic()) {
      return {
        title: 'عمليات البحث الأخيرة',
        clear: 'مسح السجل'
      };
    }

    return {
      title: 'Recent Searches',
      clear: 'Clear history'
    };
  }

  /* ==========================================================
   * SESSION STORAGE
   * ========================================================== */

  function getSearchHistory() {
    try {
      const stored =
        sessionStorage.getItem(
          STORAGE_KEY
        );

      if (!stored) {
        return [];
      }

      const parsed =
        JSON.parse(stored);

      if (!Array.isArray(parsed)) {
        return [];
      }

      return parsed
        .filter(function (item) {
          return (
            typeof item === 'string' &&
            item.trim().length > 0
          );
        })
        .slice(0, MAX_SEARCHES);
    } catch (error) {
      return [];
    }
  }

  function saveSearchHistory(history) {
    try {
      sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(
          history.slice(
            0,
            MAX_SEARCHES
          )
        )
      );
    } catch (error) {
      console.warn(
        '[Search History] Could not save history.',
        error
      );
    }
  }

  function normalizeTerm(term) {
    return String(term || '')
      .trim()
      .replace(/\s+/g, ' ')
      .toLocaleLowerCase();
  }

  function addSearchTerm(term) {
    const cleanTerm =
      String(term || '')
        .trim()
        .replace(/\s+/g, ' ');

    if (!cleanTerm) {
      return;
    }

    const normalizedNewTerm =
      normalizeTerm(cleanTerm);

    let history =
      getSearchHistory();

    /**
     * Remove older duplicate.
     *
     * Example:
     * "Blue Lenses"
     * and
     * "blue lenses"
     *
     * are treated as the same search.
     */
    history =
      history.filter(
        function (existingTerm) {
          return (
            normalizeTerm(existingTerm) !==
            normalizedNewTerm
          );
        }
      );

    /**
     * Newest search first.
     */
    history.unshift(
      cleanTerm
    );

    saveSearchHistory(
      history
    );

    log(
      'Saved:',
      cleanTerm
    );
  }

  function clearSearchHistory() {
    try {
      sessionStorage.removeItem(
        STORAGE_KEY
      );
    } catch (error) {
      // Ignore.
    }

    removeAllRecentSearchUI();

    log(
      'History cleared.'
    );
  }

  /* ==========================================================
   * STYLES
   * ========================================================== */

  function injectStyles() {
    if (
      document.getElementById(
        'search-history-styles'
      )
    ) {
      return;
    }

    const style =
      document.createElement('style');

    style.id =
      'search-history-styles';

    style.textContent = `
      .search-history {
        width: 100%;
        margin-top: 1rem;
        padding: 1.6rem 2rem 2rem;
        box-sizing: border-box;
        background: rgb(var(--color-background));
        color: rgb(var(--color-foreground));
        text-align: start;
      }

      .search-history__header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 1.5rem;
        margin-bottom: 1rem;
      }

      .search-history__title {
        margin: 0;
        font-size: 1.4rem;
        font-weight: 600;
        line-height: 1.4;
      }

      .search-history__clear {
        appearance: none;
        border: 0;
        padding: 0;
        margin: 0;
        background: transparent;
        color: rgba(var(--color-foreground), 0.7);
        text-decoration: underline;
        text-underline-offset: 0.3rem;
        cursor: pointer;
        font: inherit;
        font-size: 1.2rem;
      }

      .search-history__clear:hover {
        color: rgb(var(--color-foreground));
      }

      .search-history__list {
        display: flex;
        flex-direction: column;
        gap: 0;
        margin: 0;
        padding: 0;
        list-style: none;
      }

      .search-history__item {
        margin: 0;
        padding: 0;
      }

      .search-history__link {
        display: flex;
        align-items: center;
        width: 100%;
        min-height: 4.4rem;
        padding: 0.8rem 0;
        border: 0;
        border-bottom: 0.1rem solid rgba(var(--color-foreground), 0.08);
        background: transparent;
        color: rgb(var(--color-foreground));
        cursor: pointer;
        text-align: start;
        font: inherit;
        font-size: 1.4rem;
        line-height: 1.4;
      }

      .search-history__link:hover {
        opacity: 0.7;
      }

      .search-history__item:last-child
      .search-history__link {
        border-bottom: 0;
      }

      html[dir="rtl"] .search-history,
      html[lang^="ar"] .search-history {
        direction: rtl;
        text-align: right;
      }

      html[dir="rtl"] .search-history__link,
      html[lang^="ar"] .search-history__link {
        text-align: right;
      }
    `;

    document.head.appendChild(
      style
    );
  }

  /* ==========================================================
   * SEARCH HELPERS
   * ========================================================== */

  function isSearchInput(element) {
    if (
      !element ||
      !(element instanceof HTMLInputElement)
    ) {
      return false;
    }

    if (
      element.name !== 'q'
    ) {
      return false;
    }

    const form =
      element.closest('form');

    if (!form) {
      return false;
    }

    const action =
      form.getAttribute('action') || '';

    return (
      action.includes('/search') ||
      form.getAttribute('role') === 'search'
    );
  }

  function getSearchForm(input) {
    return input?.closest('form') || null;
  }

  /**
   * Find a visible place to insert the history.
   *
   * IMPORTANT:
   * We deliberately avoid Sense's hidden
   * predictive-search results container.
   */
  function getResultsContainer(input) {
    if (!input) {
      return null;
    }

    /**
     * Header search modal.
     */
    const searchModal =
      input.closest(
        'details-modal, .search-modal'
      );

    if (searchModal) {
      const form =
        input.closest('form');

      if (form && form.parentElement) {
        return form.parentElement;
      }
    }

    /**
     * Search results page.
     */
    const searchTemplate =
      input.closest(
        '.template-search__search'
      );

    if (searchTemplate) {
      return searchTemplate;
    }

    /**
     * Predictive-search fallback.
     */
    const predictiveSearch =
      input.closest(
        'predictive-search'
      );

    if (predictiveSearch) {
      return predictiveSearch;
    }

    /**
     * Generic fallback.
     */
    const form =
      input.closest('form');

    if (
      form &&
      form.parentElement
    ) {
      return form.parentElement;
    }

    return null;
  }

  /* ==========================================================
   * UI
   * ========================================================== */

  function removeAllRecentSearchUI() {
    document
      .querySelectorAll(
        '.search-history'
      )
      .forEach(
        function (element) {
          element.remove();
        }
      );
  }

  function createRecentSearchUI(input) {
    if (
      !input ||
      !isSearchInput(input)
    ) {
      return;
    }

    const history =
      getSearchHistory();

    if (
      history.length === 0
    ) {
      removeAllRecentSearchUI();
      return;
    }

    /**
     * Only show history when the search field
     * is empty.
     */
    if (
      String(
        input.value || ''
      ).trim()
    ) {
      removeAllRecentSearchUI();
      return;
    }

    const container =
      getResultsContainer(input);

    if (!container) {
      log(
        'No suitable container found.'
      );
      return;
    }

    /**
     * Remove previous copy.
     */
    removeAllRecentSearchUI();

    const translations =
      getTranslations();

    const wrapper =
      document.createElement(
        'div'
      );

    wrapper.className =
      'search-history';

    wrapper.setAttribute(
      'data-search-history',
      ''
    );

    /* --------------------------
     * Header
     * -------------------------- */

    const header =
      document.createElement(
        'div'
      );

    header.className =
      'search-history__header';

    const title =
      document.createElement(
        'p'
      );

    title.className =
      'search-history__title';

    title.textContent =
      translations.title;

    const clearButton =
      document.createElement(
        'button'
      );

    clearButton.type =
      'button';

    clearButton.className =
      'search-history__clear';

    clearButton.textContent =
      translations.clear;

    clearButton.setAttribute(
      'aria-label',
      translations.clear
    );

    header.appendChild(
      title
    );

    header.appendChild(
      clearButton
    );

    /* --------------------------
     * Search list
     * -------------------------- */

    const list =
      document.createElement(
        'ul'
      );

    list.className =
      'search-history__list';

    history.forEach(
      function (term) {
        const listItem =
          document.createElement(
            'li'
          );

        listItem.className =
          'search-history__item';

        const button =
          document.createElement(
            'button'
          );

        button.type =
          'button';

        button.className =
          'search-history__link';

        button.textContent =
          term;

        button.dataset.searchTerm =
          term;

        listItem.appendChild(
          button
        );

        list.appendChild(
          listItem
        );
      }
    );

    wrapper.appendChild(
      header
    );

    wrapper.appendChild(
      list
    );

    /**
     * Insert history directly below the actual
     * search form.
     *
     * This avoids Sense hiding it inside the
     * predictive-search results area.
     */
    const form =
      input.closest('form');

    if (
      form &&
      form.parentElement === container
    ) {
      form.insertAdjacentElement(
        'afterend',
        wrapper
      );
    } else {
      container.appendChild(
        wrapper
      );
    }

    log(
      'Recent searches shown.'
    );
  }

  /* ==========================================================
   * PERFORM A SAVED SEARCH
   * ========================================================== */

  function performSearch(
    term,
    sourceButton
  ) {
    addSearchTerm(
      term
    );

    const searchArea =
      sourceButton.closest(
        'predictive-search, details-modal, .search-modal, form'
      );

    let input = null;

    if (searchArea) {
      input =
        searchArea.querySelector(
          'input[name="q"]'
        );
    }

    /**
     * Fallback to visible Shopify search input.
     */
    if (!input) {
      const inputs =
        Array.from(
          document.querySelectorAll(
            'input[name="q"]'
          )
        );

      input =
        inputs.find(
          function (candidate) {
            return (
              isSearchInput(candidate) &&
              candidate.offsetParent !== null
            );
          }
        ) || null;
    }

    if (
      input &&
      getSearchForm(input)
    ) {
      const form =
        getSearchForm(input);

      input.value =
        term;

      /**
       * Tell Sense the value changed.
       */
      input.dispatchEvent(
        new Event(
          'input',
          {
            bubbles: true
          }
        )
      );

      /**
       * Submit normal Shopify search.
       */
      if (
        typeof form.requestSubmit ===
        'function'
      ) {
        form.requestSubmit();
      } else {
        form.submit();
      }

      return;
    }

    /**
     * Final fallback.
     */
    const root =
      window.Shopify?.routes?.root ||
      window.routes?.root_url ||
      '/';

    const searchUrl =
      new URL(
        root + 'search',
        window.location.origin
      );

    searchUrl.searchParams.set(
      'q',
      term
    );

    window.location.href =
      searchUrl.toString();
  }

  /* ==========================================================
   * SAVE NORMAL SEARCH SUBMISSIONS
   * ========================================================== */

  document.addEventListener(
    'submit',
    function (event) {
      const form =
        event.target;

      if (
        !(form instanceof HTMLFormElement)
      ) {
        return;
      }

      const input =
        form.querySelector(
          'input[name="q"]'
        );

      if (
        !input ||
        !isSearchInput(input)
      ) {
        return;
      }

      const term =
        input.value.trim();

      if (!term) {
        return;
      }

      addSearchTerm(
        term
      );
    },
    true
  );

  /* ==========================================================
   * ALSO SAVE SEARCH QUERY FROM SEARCH RESULTS URL
   * ========================================================== */

  function saveSearchFromCurrentUrl() {
    if (
      window.location.pathname.indexOf(
        '/search'
      ) === -1
    ) {
      return;
    }

    try {
      const params =
        new URLSearchParams(
          window.location.search
        );

      const query =
        params.get('q');

      if (query) {
        addSearchTerm(
          query
        );
      }
    } catch (error) {
      // Ignore.
    }
  }

  /* ==========================================================
   * SHOW HISTORY ON FOCUS
   * ========================================================== */

  document.addEventListener(
    'focusin',
    function (event) {
      const input =
        event.target;

      if (
        !isSearchInput(input)
      ) {
        return;
      }

      if (
        input.value.trim()
      ) {
        return;
      }

      setTimeout(
        function () {
          createRecentSearchUI(
            input
          );
        },
        50
      );
    }
  );

  /* ==========================================================
   * SHOW / HIDE WHILE USER TYPES
   * ========================================================== */

  document.addEventListener(
    'input',
    function (event) {
      const input =
        event.target;

      if (
        !isSearchInput(input)
      ) {
        return;
      }

      const value =
        input.value.trim();

      if (value) {
        removeAllRecentSearchUI();
        return;
      }

      setTimeout(
        function () {
          if (
            !input.value.trim()
          ) {
            createRecentSearchUI(
              input
            );
          }
        },
        100
      );
    }
  );

  /* ==========================================================
   * CLICK RECENT SEARCH / CLEAR HISTORY
   * ========================================================== */

  document.addEventListener(
    'click',
    function (event) {
      const target =
        event.target instanceof Element
          ? event.target
          : null;

      if (!target) {
        return;
      }

      /**
       * Recent search item.
       */
      const recentSearchButton =
        target.closest(
          '.search-history__link'
        );

      if (recentSearchButton) {
        event.preventDefault();
        event.stopPropagation();

        const term =
          recentSearchButton.dataset
            .searchTerm;

        if (term) {
          performSearch(
            term,
            recentSearchButton
          );
        }

        return;
      }

      /**
       * Clear history.
       */
      const clearButton =
        target.closest(
          '.search-history__clear'
        );

      if (clearButton) {
        event.preventDefault();
        event.stopPropagation();

        clearSearchHistory();

        return;
      }
    }
  );

  /* ==========================================================
   * HANDLE SEARCH MODAL OPENING
   * ========================================================== */

  document.addEventListener(
    'click',
    function () {
      /**
       * Sense moves focus into the search field
       * shortly after the modal opens.
       */
      setTimeout(
        function () {
          const inputs =
            Array.from(
              document.querySelectorAll(
                'input[name="q"]'
              )
            );

          const visibleInput =
            inputs.find(
              function (input) {
                return (
                  isSearchInput(input) &&
                  input.offsetParent !== null &&
                  !input.value.trim()
                );
              }
            );

          if (visibleInput) {
            createRecentSearchUI(
              visibleInput
            );
          }
        },
        250
      );
    }
  );

  /* ==========================================================
   * HANDLE DYNAMIC SENSE SEARCH MODAL
   * ========================================================== */

  let modalTimer = null;

  const observer =
    new MutationObserver(
      function () {
        clearTimeout(
          modalTimer
        );

        modalTimer =
          setTimeout(
            function () {
              const inputs =
                Array.from(
                  document.querySelectorAll(
                    'input[name="q"]'
                  )
                );

              const visibleEmptyInput =
                inputs.find(
                  function (input) {
                    return (
                      isSearchInput(input) &&
                      input.offsetParent !== null &&
                      !input.value.trim() &&
                      (
                        document.activeElement ===
                          input ||
                        input.closest(
                          'details[open], .search-modal'
                        )
                      )
                    );
                  }
                );

              if (
                visibleEmptyInput &&
                getSearchHistory().length > 0 &&
                !document.querySelector(
                  '.search-history'
                )
              ) {
                createRecentSearchUI(
                  visibleEmptyInput
                );
              }
            },
            100
          );
      }
    );

  /* ==========================================================
   * INITIALIZE
   * ========================================================== */

  function initialize() {
    injectStyles();

    /**
     * If user is already on /search?q=...
     * save that search too.
     */
    saveSearchFromCurrentUrl();

    observer.observe(
      document.body,
      {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: [
          'open'
        ]
      }
    );

    log(
      'Loaded successfully.'
    );
  }

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      initialize,
      {
        once: true
      }
    );
  } else {
    initialize();
  }
})();